// Pricing.js — AI Credits Pricing Page
// Visible to all (no auth required), mostly used to attract non-subscribed teachers.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiCreditsAPI } from '../services/api';
import '../styles/Pricing.css';
import { FaCheck, FaBrain, FaWhatsapp, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '+4915209451229';

const FAQ_ITEMS = [
  {
    q: 'ما هو الكريدت؟',
    a: 'الكريدت هو وحدة استخدام الذكاء الاصطناعي في دارِس. كل كريدت يُستخدم لتصحيح إجابة طالب واحد على سؤال مقالي واحد. مثلاً: 10 طلاب يجيبون على سؤالين مقاليين = 20 كريدت.',
  },
  {
    q: 'كيف أشحن رصيدي؟',
    a: 'اختر الباقة المناسبة لك، ثم أرسل طلب الشحن من داخل لوحة التحكم. ستحصل على رقم طلب فريد، أرسله عبر واتساب مع إثبات الدفع. سيتم إضافة رصيدك خلال 24 ساعة.',
  },
  {
    q: 'هل ينتهي صلاحية الكريدت؟',
    a: 'لا، الكريدت لا تنتهي صلاحيته. يبقى في حسابك حتى تستخدمه.',
  },
  {
    q: 'ماذا يحدث إذا نفد الرصيد؟',
    a: 'عند نفاد الرصيد، لن يتمكن الذكاء الاصطناعي من تصحيح الأسئلة المقالية تلقائياً. يمكنك شحن رصيدك في أي وقت.',
  },
  {
    q: 'هل يمكنني تجربة النظام مجاناً؟',
    a: 'نعم! كل معلم جديد يحصل على 10 كريدت مجانية عند التسجيل لتجربة تصحيح الأسئلة المقالية بالذكاء الاصطناعي.',
  },
  {
    q: 'ما هي وسائل الدفع المتاحة؟',
    a: 'حالياً نقبل الدفع الأوفلاين (تحويل بنكي / محفظة إلكترونية) مع التأكيد عبر واتساب. قريباً سنضيف وسائل دفع إلكترونية مباشرة.',
  },
];

const PACKAGE_ICONS = {
  'Starter': '🚀',
  'Teacher': '👩‍🏫',
  'Pro': '⚡',
};

const PACKAGE_FEATURES = {
  'Starter': ['50 تصحيحًا مقاليًا', 'مثالية للبداية والتجربة', 'لا انتهاء للصلاحية', 'دعم عبر واتساب'],
  'Teacher': ['200 تصحيحًا مقاليًا', 'الأنسب للمعلم النشط', 'لا انتهاء للصلاحية', 'أولوية في الدعم'],
  'Pro': ['500 تصحيحًا مقاليًا', 'للمعلمين ذوي الأعباء الكبيرة', 'لا انتهاء للصلاحية', 'دعم متقدم'],
};

// ── FAQ accordion ──
const FaqItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="pricing-faq-item">
      <button className={`pricing-faq-q ${open ? 'open' : ''}`} onClick={() => setOpen(!open)} type="button">
        {item.q}
        {open ? <FaChevronUp size={13} /> : <FaChevronDown size={13} />}
      </button>
      {open && <div className="pricing-faq-a">{item.a}</div>}
    </div>
  );
};

// ── Main Component ──
const Pricing = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    aiCreditsAPI.getPackages()
      .then((r) => setPackages(r.data))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  // Default packages in case API fails
  const displayPkgs = packages.length > 0 ? packages : [
    { id: 1, name: 'Starter', credits: 50, price: 5, badge: null, description: '50 تصحيحًا للإجابات المقالية' },
    { id: 2, name: 'Teacher', credits: 200, price: 15, badge: 'الأشهر', description: '200 تصحيحًا — الأكثر شيوعًا' },
    { id: 3, name: 'Pro', credits: 500, price: 30, badge: 'أفضل قيمة', description: '500 تصحيحًا للأعباء الكبيرة' },
  ];

  return (
    <div className="pricing-page">
      {/* ── Hero ── */}
      <div className="pricing-hero">
        <div className="pricing-hero-content">
          <div className="pricing-hero-badge">
            <FaBrain /> نظام تصحيح مقالي بالذكاء الاصطناعي
          </div>
          <h1>
            اجعل التصحيح <span>أسرع وأدق</span><br />مع الذكاء الاصطناعي
          </h1>
          <p>
            وفّر ساعات من التصحيح اليدوي. الذكاء الاصطناعي في دارِس يُصحح الأسئلة المقالية
            بدقة عالية ويعطي المعلم تقريرًا تفصيليًا لكل إجابة.
          </p>
        </div>
      </div>

      <div className="pricing-section">
        {/* ── Credit Info ── */}
        <div className="pricing-credit-info">
          <div className="pricing-credit-info-icon">🧮</div>
          <div className="pricing-credit-info-text">
            <h3>كيف تُحسب الكريدت؟</h3>
            <p>
              <strong>1 كريدت = تصحيح إجابة طالب واحد على سؤال مقالي واحد.</strong>{' '}
              مثال: امتحان به سؤالان مقاليان و10 طلاب = 20 كريدت فقط.
              المعلم يتحكم في متى يُفعّل التصحيح الآلي.
            </p>
          </div>
        </div>

        {/* ── Packages ── */}
        <div className="pricing-cards-grid">
          {loading ? (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              ⏳ جارٍ التحميل...
            </div>
          ) : (
            displayPkgs.map((pkg, i) => {
              const isFeatured = pkg.badge === 'الأشهر';
              const icon = PACKAGE_ICONS[pkg.name] || '✨';
              const features = PACKAGE_FEATURES[pkg.name] || [];
              const perCredit = (pkg.price / pkg.credits).toFixed(3);

              return (
                <div key={pkg.id} className={`pricing-card ${isFeatured ? 'featured' : ''}`}>
                  {pkg.badge && <div className="pricing-card-ribbon">{pkg.badge}</div>}
                  <div className="pricing-card-icon">{icon}</div>
                  <div className="pricing-card-name">{pkg.name}</div>

                  <div className="pricing-card-credits">{pkg.credits.toLocaleString()}</div>
                  <div className="pricing-card-credits-sub">تصحيح مقالي</div>

                  <div className="pricing-card-price-wrap">
                    <div className="pricing-card-price">
                      <sup>$</sup>{pkg.price}
                    </div>
                    <div className="pricing-card-per">لمرة واحدة</div>
                    <div className="pricing-card-per-credit">${perCredit} / كريدت</div>
                  </div>

                  <ul className="pricing-card-features">
                    {features.map((f, fi) => (
                      <li key={fi}>
                        <FaCheck className="pricing-check" /> {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    className={`pricing-card-btn ${isFeatured ? 'primary' : 'outline'}`}
                    onClick={() => navigate('/ai-credits')}
                    type="button"
                  >
                    اشترِ الآن
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* ── How it works ── */}
        <div className="pricing-how">
          <h2>⚙️ كيف تعمل عملية الشحن؟</h2>
          <div className="pricing-steps">
            <div className="pricing-step">
              <div className="pricing-step-num">1</div>
              <div className="pricing-step-title">اختر الباقة</div>
              <div className="pricing-step-desc">اختر الباقة المناسبة لك من داخل لوحة التحكم</div>
            </div>
            <div className="pricing-step">
              <div className="pricing-step-num">2</div>
              <div className="pricing-step-title">أرسل الطلب</div>
              <div className="pricing-step-desc">ستُنشئ رقم طلب فريد يُستخدم لتأكيد دفعتك</div>
            </div>
            <div className="pricing-step">
              <div className="pricing-step-num">3</div>
              <div className="pricing-step-title">تواصل عبر واتساب</div>
              <div className="pricing-step-desc">أرسل رقم الطلب + إثبات الدفع على واتساب الإدارة</div>
            </div>
            <div className="pricing-step">
              <div className="pricing-step-num">4</div>
              <div className="pricing-step-title">استلم رصيدك</div>
              <div className="pricing-step-desc">تُضاف الكريدت خلال 24 ساعة إلى حسابك تلقائياً</div>
            </div>
          </div>
        </div>

        {/* ── FAQ ── */}
        <div className="pricing-faq">
          <h2>❓ الأسئلة الشائعة</h2>
          {FAQ_ITEMS.map((item, i) => <FaqItem key={i} item={item} />)}
        </div>

        {/* ── CTA ── */}
        <div className="pricing-cta">
          <h2>🚀 ابدأ الآن وجرّب مجاناً</h2>
          <p>سجّل كمعلم واحصل على 10 كريدت مجانية فور تفعيل حسابك</p>
          <button
            className="pricing-cta-btn"
            onClick={() => navigate('/ai-credits')}
            type="button"
          >
            <FaBrain /> انتقل إلى رصيدي
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
